import React, { useEffect, useRef } from 'react';
import { Provider } from 'react-redux';
import * as Sentry from '@sentry/react';
import { PersistGate } from 'redux-persist/integration/react';
import { ToastContainer } from 'react-toastify';
import isPropValid from '@emotion/is-prop-valid';
import { StyleSheetManager } from 'styled-components';
import isEqual from 'lodash.isequal';
import debug from 'debug';
import 'react-toastify/dist/ReactToastify.css';

// Configuración de logs con namespaces para el componente App
const logAuth = debug('rois:app:auth');

// Habilitar logs solo en desarrollo
if (import.meta.env.DEV) {
  // Habilitar logs de debug para actions y app
  localStorage.setItem('debug', 'rois:actions:*,rois:app:*');
  debug.enable('rois:actions:*,rois:app:*');
}

// This implements the default behavior from styled-components v5
function shouldForwardProp(propName: string, target: any) {
  if (typeof target === 'string') {
    // For HTML elements, forward the prop if it is a valid HTML attribute
    return isPropValid(propName);
  }
  // For other elements, forward all props
  return true;
}

// Global Styles
import { GlobalStyle, ReuseComponent } from './styles/global-styles';

// Context
import { AuthProvider, useAuth } from './hooks/useAuth';
import { AppConfigProvider } from './hooks/useAppConfig';

// Theme
import { ThemeProvider } from './styles/theme';

// Apollo
import { AppApolloProvider } from './gql/client';

// Routes
import Router from './router';

// Store
import { store, persistor } from './state/configureStore';

// Components
import { toastContainerConfig } from './components/toasts/MainToast/utils';
import SuperModal from './components/modals/SuperModal';

// Pages
import { AppErrorFallback } from './pages/utils/Error';

// Offline
import { OfflineBanner } from './offline/OfflineBanner';

// Splash de arranque (evita el blanco puro durante la rehidratación)
import { SplashScreen } from './components/display/SplashScreen';

// Version Manager
import {
  initializeVersionManager,
  versionManager,
} from './utils/versionManager';

// Componente interno que tiene acceso al contexto de autenticación
const AppContent = React.memo(() => {
  const authContext = useAuth();
  const previousAuthContext = useRef<typeof authContext | null>(null);

  logAuth(' AuthContext', authContext);

  useEffect(() => {
    // 🔍 Comparar authContext actual con el anterior usando isEqual
    const hasAuthContextChanged = !isEqual(
      authContext,
      previousAuthContext.current
    );

    logAuth('🔍 [APP] Auth Context Comparison:');
    logAuth('   📝 Previous:', previousAuthContext.current);
    logAuth('   📝 Current:', authContext);
    logAuth('   🔄 Has changed:', hasAuthContextChanged);

    // 🔒 Si no ha cambiado el authContext, no reinicializar
    if (!hasAuthContextChanged) {
      logAuth('⚠️ [APP] AuthContext unchanged, skipping initialization...');
      return;
    }

    // 🚀 INICIALIZACIÓN DEL VERSION MANAGER (cuando authContext cambia)
    logAuth('🚀 [APP] AuthContext changed, initializing VersionManager...');

    // 🔒 Actualizar la referencia del authContext anterior
    previousAuthContext.current = authContext;

    // 1️⃣ Inicializar el gestor de versiones base
    initializeVersionManager(store, authContext);

    // 2️⃣ Configurar detección automática de GitHub Deployments
    const currentMode = import.meta.env.MODE; // 'development', 'beta', 'production'

    logAuth('🔧 ===== CONFIGURING GITHUB DEPLOYMENTS DETECTION =====');
    logAuth(`   📝 MODE: ${currentMode}`);

    // 🎯 Configuración basada únicamente en currentMode
    let targetEnvironment: 'production' | 'beta' = 'production';
    let pollInterval = 10 * 60 * 1000; // 10 minutos por defecto
    let enableAutoCheck = false;

    switch (currentMode) {
      case 'beta':
        targetEnvironment = 'beta';
        pollInterval = 1 * 60 * 1000; // 1 minutos para testing
        enableAutoCheck = false;
        break;

      case 'production':
        targetEnvironment = 'production';
        pollInterval = 3 * 60 * 1000; // 3 minutos en producción
        // El aviso web lo da `useAppUpdate` leyendo `/version.json` del propio
        // dominio (spec 2026-09-30 § 5.1). Este sondeo al backend —y su
        // respaldo a la API de GitHub con `VITE_GITHUB_TOKEN`— queda APAGADO,
        // no borrado: el usuario decidió mantener el token y su código (D0,
        // 1-oct). Si se encendiera, dos sistemas abrirían el mismo aviso.
        enableAutoCheck = false;
        break;

      case 'development':
      default:
        targetEnvironment = 'beta'; // Monitorea beta desde desarrollo
        pollInterval = 1 * 60 * 1000; // 1 minuto para testing rápido
        enableAutoCheck = false; // ACTIVADO en desarrollo para testing
        break;
    }

    logAuth(`📊 Configuration determined:`);
    logAuth(`   🎯 Target environment: ${targetEnvironment}`);
    logAuth(`   ⏰ Poll interval: ${pollInterval / 60000} min`);
    logAuth(`   🔄 Auto-check enabled: ${enableAutoCheck ? 'YES' : 'NO'}`);
    logAuth(
      `   🔑 GitHub token: ${import.meta.env.VITE_GITHUB_TOKEN ? 'CONFIGURED' : 'NOT SET'
      }`
    );

    // ✅ Configurar detección automática
    logAuth('🚀 Calling versionManager.configureGitHubDeploymentDetection...');
    versionManager.configureGitHubDeploymentDetection({
      githubRepo: 'Insolite-Solutions-SL/rois-front',
      environment: targetEnvironment,
      pollInterval,
      enableAutoCheck,
      githubToken: import.meta.env.VITE_GITHUB_TOKEN,
    });

    logAuth(
      '✅ versionManager.configureGitHubDeploymentDetection call completed'
    );
    logAuth('===============================================');
  }, [authContext]); // 🔒 Depende de authContext para manejar cambios de login/logout

  return (
    <>
      <GlobalStyle />
      <OfflineBanner />
      <Router />
      <ReuseComponent>
        <ToastContainer {...toastContainerConfig} />
        <SuperModal />
      </ReuseComponent>
    </>
  );
});

export default function App() {
  return (
    <StyleSheetManager shouldForwardProp={shouldForwardProp}>
      <Provider store={store}>
        <PersistGate loading={<SplashScreen />} persistor={persistor}>
          <ThemeProvider initialBrand="rois" initialMode="light">
            <AuthProvider>
              <AppApolloProvider>
                <AppConfigProvider>
                  {/* Sin `useRouteError`: este límite envuelve también lo
                      que vive fuera del router (QA 15-oct, T2.10+vacío). */}
                  <Sentry.ErrorBoundary
                    fallback={({ error }) => <AppErrorFallback error={error} />}
                  >
                    <AppContent />
                  </Sentry.ErrorBoundary>
                </AppConfigProvider>
              </AppApolloProvider>
            </AuthProvider>
          </ThemeProvider>
        </PersistGate>
      </Provider>
    </StyleSheetManager>
  );
}
